<template>
  <div
    v-if="isAuthorized"
    class="w-full p-3 md:p-6 bg-white rounded-xl shadow-pm"
  >
    <div class="flex justify-between">
      <div
        class="text-lg mr-10 mb-3.5 md:mb-0 flex gap-x-4 items-center font-semibold xl:text-xl xl:font-bold"
      >
        Daily Price
      </div>
      <Filters
        v-if="!isMobile"
        :key="dateRangeName"
        :raw-data="dataForXlsx"
        :category="category"
        :title="`Daily Price ${category}`"
        :initial-date-range-name="dateRangeName"
        :date-range="dateRange"
        @on-date-range-change="onDateRangeChange"
      />
    </div>

    <div class="grid grid-cols-10 w-full gap-x-2 relative">
      <Values
        v-if="color"
        class="col-span-4 md:col-span-3"
        :series-name="summaryData.name"
        :today-value="summaryData.todayValue"
        :latest-value="summaryData.latestChange"
        :latest-color="color"
      />

      <div
        v-if="!loading"
        class="relative col-span-6 md:col-span-7 h-24 md:h-48"
      >
        <div class="absolute -top-4 md:top-0 right-0">
          <LastChange
            v-if="defaultDateRange"
            :latest-color="defaultDateRange.color"
            :latest-value="defaultDateRange.value"
            :label="defaultDateRange.label"
            class="justify-end"
          />
          <LastChange
            v-if="dateRangeValues && chartData.datasets.length !== 0"
            :latest-color="dateRangeValues.color"
            :latest-value="dateRangeValues.value"
            :label="dateRangeValues.label"
            class="justify-end"
          />
        </div>
        <GradientLineChart
          v-if="chartData.datasets.length"
          ref="lineChart"
          css-classes="h-24 md:h-48 w-full"
          :chart-data="chartData"
          :is-mobile="isMobile"
          :min-y="minMax.min"
          :max-y="minMax.max"
          :tooltip-value-symbol="summaryData.valueSymbol"
          @on-hover="onHover"
        />
      </div>
      <div
        v-else
        class="col-span-6 md:col-span-7 h-24 md:h-48 w-full flex justify-center"
      >
        <TheLoader size="small" />
      </div>
    </div>

    <div v-if="isMobile" class="flex justify-end">
      <Filters
        :key="dateRangeName"
        class="mt-3.5"
        :raw-data="dataForXlsx"
        :category="category"
        :title="`Daily Price ${category}`"
        :initial-date-range-name="dateRangeName"
        :date-range="dateRange"
        @on-date-range-change="onDateRangeChange"
        @on-chart-download="onChartDownload"
      />
    </div>
  </div>
</template>
<script setup lang="ts">
/** packages */

/** components */
import TheLoader from '~/components/TheLoader.vue'
import GradientLineChart from '~/components/price-assessments/GradientLineChart.vue'
import Values from '@/components/portals/DailyIndicator/Values.vue'
import Filters from '@/components/portals/DailyIndicator/Filter.vue'
import LastChange from '~/components/portals/DailyIndicator/LastChange.vue'

/** composables */
import { useScreenSize } from '~/composables/useScreenSize'

/** utils */
import { getDateRange } from '~/utils/date'
import { logError } from '~/utils/log-error'
import { numberFormatter } from '~/utils/number'

/** types */
import type { TGeneral } from '~/src/types/common'
import type { Color, Filter } from '~/types/daily-indicator.type'
import type { DateRangeType } from '~/types/date-range.type'

/** constants */

const ZERO = 0
const colors: Record<string, Color> = {
  positive: { color: '#4bbf7c', colorType: 'positive' },
  negative: { color: '#d94761', colorType: 'negative' },
  neutral: { color: '#72747b', colorType: 'neutral' },
}
const ranges = {
  oneWeek: { id: 'weeklyChange', label: 'Last week' },
  oneMonth: { id: 'monthlyChange', label: 'Last month' },
  threeMonths: { id: 'quarterlyMonthsChange', label: 'Last 3 months' },
  yearToDate: { id: 'yearToDate', label: 'Year to Date' },
}

/** app */
const { $restClient, $rudderstack } = useNuxtApp()
const { isMobile } = useScreenSize()

const dateRangeValues = ref(null)
const color = ref<Color>()
const dateRangeName = ref<Filter>('oneWeek')
const dateRange = ref<DateRangeType>(getDateRange(dateRangeName.value))
const summaryData = ref({
  name: '',
  valueSymbol: '',
  todayValue: '',
  latestChange: {
    unit: '',
    value: 0,
  },
})
const isFirstLoad = ref(true)
const loading = ref(false)
const rawData = ref(null)
const category = 'lithium'
const isAuthorized = ref(false)
const chartData = ref({
  labels: [],
  datasets: [],
})
const minMax = ref({ min: null, max: null })
const dataForXlsx = ref({})
const isYearToDate = computed(() => {
  const currentRange = ranges[dateRangeName.value]
  return currentRange.id === ranges.yearToDate.id
})
const defaultDateRange = computed(() => {
  const currentRange = ranges[dateRangeName.value]
  if (chartData.value.datasets.length !== 0 || !isYearToDate.value) return null

  return {
    label: currentRange.label,
    color: colors.neutral,
    value: {
      value: '- -',
      unit: '%',
    },
  }
})

const getDateRangeValues = (rangeName: Filter, summaryData) => {
  const currentRange = ranges[rangeName]

  if (!currentRange) return null

  const currentValue = summaryData[currentRange.id]

  if (!currentValue)
    return {
      label: currentRange.label,
      color: colors.neutral,
      value: {
        value: '',
        unit: '',
      },
    }

  return {
    label: currentRange.label,
    color: getColor(currentValue.value),
    value: currentValue,
  }
}

const getColor = (value: number) => {
  if (value === ZERO || value === null) return colors.neutral
  if (value > ZERO) return colors.positive
  return colors.negative
}

const onDateRangeChange = async (rangeName: Filter) => {
  dateRange.value = getDateRange(rangeName)
  dateRangeName.value = rangeName
  await fetchDailyPriceIndicator()
}

const onChartDownload = () => {
  $rudderstack.trackChartDownload({
    chart_title: 'Daily Price Chart',
    chart_type: 'line',
    download_format: 'xlsx',
  })
}

const getSummaryValues = (summary) => {
  const item = summary.series[0]
  const todayValue = numberFormatter(item?.data?.today || 0, 2)
  return {
    name: `Lithium Carbonate, ${item?.name}`,
    valueSymbol: item?.currency?.symbol || '',
    todayValue: `${item?.currency?.symbol}${todayValue}`,
    latestChange: item?.data?.latestChange,
    monthlyChange: item?.data?.monthlyChange,
    weeklyChange: item?.data?.weeklyChange,
    quarterlyMonthsChange: item?.data?.quarterlyMonthsChange,
    yearToDate: item?.data?.yearToDate,
  }
}

const getGradientBackground = (context: TGeneral, color: string) => {
  if (!context.chart.chartArea) return
  const {
    ctx,
    chartArea: { top, bottom },
  } = context.chart
  const gradient = ctx.createLinearGradient(0, top, 0, bottom)
  gradient.addColorStop(0, color)
  gradient.addColorStop(1, 'rgba(255, 255, 255, 0)')
  return gradient
}

const getDatasetsAndMinMax = (
  series: any[],
  color: string,
): { dataSets: any; min: number; max: number } => {
  if (!series) return { dataSets: [], min: 0, max: 0 }

  const allValues: number[] = []
  const dataSets = series.map((data) => ({
    label: `${data.category.name} ${data.shippingRoute.name}`,
    fill: true,
    yAxisID: 'y',
    digits: 0,
    unit: data.currency.symbol ?? '',
    spanGaps: true,
    lineTension: 0.3,
    borderColor: color,
    backgroundColor: (context: TGeneral) =>
      getGradientBackground(context, color),
    data: data.series.map((serie) => {
      if (typeof serie.valueMid === 'number') allValues.push(serie.valueMid)
      return { x: serie.date, y: serie.valueMid }
    }),
  }))
  const min = Math.min(...allValues)
  const max = Math.max(...allValues)
  const padding = (max - min) * 0.2

  return {
    dataSets,
    min: Math.floor((min - padding) / 100) * 100,
    max: Math.ceil((max + padding) / 100) * 100,
  }
}

const getUniqueLabels = (series: any[]) => [
  ...new Set(series.map((serie) => serie.date)),
]

const getChartData = ({ chartValues, product, color }) => {
  if (!chartValues) {
    return {
      chartData: {
        labels: [],
        datasets: [],
      },
      dataForXlsx: {},
      minMax: { min: null, max: null },
    }
  }

  const { dataSets, min, max } = getDatasetsAndMinMax(chartValues, color)
  return {
    chartData: {
      labels: getUniqueLabels(chartValues[0].series),
      datasets: dataSets,
    },
    dataForXlsx: getAdaptedPrices({
      $metadata: { product },
      data: rawData.value.chart,
    }),
    minMax: { min, max },
  }
}

const fetchDailyPriceIndicator = async () => {
  loading.value = true
  try {
    rawData.value = await $restClient.getDailyIndexByProduct(
      category,
      dateRange.value,
    )

    summaryData.value = getSummaryValues(rawData.value.summary[0])
    color.value = getColor(summaryData.value.latestChange.value)
    isAuthorized.value = true
    dateRangeValues.value = getDateRangeValues(
      dateRangeName.value,
      summaryData.value,
    )

    const {
      chartData: chartDataValue,
      dataForXlsx: dataForXlsxValue,
      minMax: minMaxValue,
    } = getChartData({
      chartValues: rawData.value.chart,
      product: rawData.value.product,
      color: dateRangeValues.value.color.color,
    })

    chartData.value = chartDataValue
    dataForXlsx.value = dataForXlsxValue
    minMax.value = minMaxValue
  } catch (error) {
    isAuthorized.value = false
    logError(error)
  } finally {
    if (!isFirstLoad.value) {
      $rudderstack.trackFilterApplied({
        filter_values: {
          date_range: dateRangeName.value,
          date_range_name: dateRangeName.value,
        },
        filter_type: 'date_range_filter',
        chart_title: 'Daily Price Chart',
      })
    }
    loading.value = false
    isFirstLoad.value = false
  }
}

const onHover = () => {
  $rudderstack.trackChartInteraction({
    chart_title: 'Daily Price Chart',
    chart_type: 'line',
    start_date: dateRange.value.from,
    end_date: dateRange.value.to,
    current_filters: {
      date_range: dateRange.value,
      date_range_name: dateRangeName.value,
    },
  })
}

onMounted(async () => {
  await fetchDailyPriceIndicator()
})
</script>
